import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ContentItem } from '../data/contentLibrary'

export type GridType = 'tian' | 'mi' | 'huigong' | 'jiugong' | 'blank'
export type StrokeType = 'color' | 'number' | 'arrow'
export type TraceType = 'hollow' | 'solid'
export type PaperSize = 'A4P' | 'A4L' | 'A3P' | 'A3L' | 'A5P' | 'A5L' | 'B4P' | 'B4L' | 'B5P' | 'B5L' | 'B5'
export type ContentSource = 'custom' | 'poem' | 'classic' | 'radical' | 'common' | 'lyrics' | 'structure' | 'sutra' | 'calligraphy' | 'control'
export type FontDirection = 'horizontal' | 'vertical'

export const DEFAULT_COPYBOOK_FONT = '"The Peak Font Plus", "Noto Sans TC", "Noto Serif TC", sans-serif'
export const FONT_GRID_RATIO = 0.85

export interface CopybookTemplatePreset {
  id: string
  name: string
  description: string
  gridType: GridType
  showTrace: boolean
  traceType: TraceType
  showStroke: boolean
  showPhonetic: boolean
  showCorrection: boolean
  colsPerRow: number
  gridSize: number
  subtitle?: string
  footer?: string
  copyInterval?: boolean
  copyBlankCount?: number
  copyRowInterval?: boolean
  copyRowBlankCount?: number
}

interface CopybookState {
  // Content
  text: string
  setText: (text: string) => void
  contentSource: ContentSource
  setContentSource: (source: ContentSource) => void
  activeContentId: string | null
  setActiveContentId: (id: string | null) => void
  title: string
  setTitle: (title: string) => void
  subtitle: string
  setSubtitle: (subtitle: string) => void
  footer: string
  setFooter: (footer: string) => void
  printTitleAndSource: boolean
  setPrintTitleAndSource: (v: boolean) => void

  // Paper
  paperSize: PaperSize
  setPaperSize: (size: PaperSize) => void
  marginTop: number
  setMarginTop: (v: number) => void
  marginBottom: number
  setMarginBottom: (v: number) => void
  marginLeft: number
  setMarginLeft: (v: number) => void
  marginRight: number
  setMarginRight: (v: number) => void
  showPageNumber: boolean
  setShowPageNumber: (v: boolean) => void

  // Grid
  gridType: GridType
  setGridType: (type: GridType) => void
  gridSize: number
  setGridSize: (v: number) => void
  colsPerRow: number
  setColsPerRow: (v: number) => void
  rowGap: number
  setRowGap: (v: number) => void
  colGap: number
  setColGap: (v: number) => void
  gridOpacity: number
  setGridOpacity: (v: number) => void
  gridRadius: number
  setGridRadius: (v: number) => void
  gridLineWidth: number
  setGridLineWidth: (v: number) => void
  gridColor: string
  setGridColor: (v: string) => void

  // Stroke
  showStroke: boolean
  setShowStroke: (v: boolean) => void
  strokeType: StrokeType
  setStrokeType: (type: StrokeType) => void
  strokeRepeat: number
  setStrokeRepeat: (v: number) => void
  highlightLastStroke: boolean
  setHighlightLastStroke: (v: boolean) => void

  // Trace
  showTrace: boolean
  setShowTrace: (v: boolean) => void
  traceType: TraceType
  setTraceType: (type: TraceType) => void
  traceColor: string
  setTraceColor: (v: string) => void
  traceLineWidth: number
  setTraceLineWidth: (v: number) => void
  exampleColor: string
  setExampleColor: (v: string) => void
  traceInterval: boolean
  setTraceInterval: (v: boolean) => void
  copyInterval: boolean
  setCopyInterval: (v: boolean) => void
  copyBlankCount: number
  setCopyBlankCount: (v: number) => void
  copyRowInterval: boolean
  setCopyRowInterval: (v: boolean) => void
  copyRowBlankCount: number
  setCopyRowBlankCount: (v: number) => void

  // Phonetic
  showPhonetic: boolean
  setShowPhonetic: (v: boolean) => void
  phoneticBlank: boolean
  setPhoneticBlank: (v: boolean) => void

  // Advanced
  showCorrection: boolean
  setShowCorrection: (v: boolean) => void
  fillLastPage: boolean
  setFillLastPage: (v: boolean) => void
  autoFill: boolean
  setAutoFill: (v: boolean) => void
  preserveLineBreaks: boolean
  setPreserveLineBreaks: (v: boolean) => void
  filterNewlinesOnClean: boolean
  setFilterNewlinesOnClean: (v: boolean) => void

  // Font
  fontFamily: string
  setFontFamily: (v: string) => void
  fontDirection: FontDirection
  setFontDirection: (v: FontDirection) => void

  // Font Management
  disabledFontIds: (number | string)[]
  toggleFontDisabled: (id: number | string) => void
  localFonts: { id: string; name: string; fontFamily: string }[]
  addLocalFont: (name: string, fontFamily: string) => void
  removeLocalFont: (id: string) => void

  // Templates
  activeTemplateId: string | null
  applyTemplate: (template: CopybookTemplatePreset) => void
  clearTemplateSelection: () => void

  // User custom items
  userItems: ContentItem[]
  addUserItem: (item: ContentItem) => void
  updateUserItem: (id: string, updates: Partial<ContentItem>) => void
  deleteUserItem: (id: string) => void

  // Advanced mode
  advancedMode: boolean
  setAdvancedMode: (v: boolean) => void
}

export const PAPER_SIZES_MM: Record<string, { width: number; height: number }> = {
  A4P: { width: 210, height: 297 }, // A4 直式
  A4L: { width: 297, height: 210 }, // A4 橫式
  A3P: { width: 297, height: 420 }, // A3 直式
  A3L: { width: 420, height: 297 }, // A3 橫式
  A5P: { width: 148, height: 210 }, // A5 直式
  A5L: { width: 210, height: 148 }, // A5 橫式
  B4P: { width: 250, height: 353 }, // B4 直式
  B4L: { width: 353, height: 250 }, // B4 橫式
  B5P: { width: 176, height: 250 }, // B5 直式
  B5L: { width: 250, height: 176 }, // B5 橫式
  // 向後相容舊版的 B5 設定
  B5: { width: 176, height: 250 },
}

export interface EffectiveLayout {
  cols: number
  rows: number
  gridSize: number
  colGap: number
  rowGap: number
  printableWidth: number
  printableHeight: number
}

export function getEffectiveLayout(store: {
  paperSize: PaperSize
  marginTop: number
  marginBottom: number
  marginLeft: number
  marginRight: number
  gridSize: number
  colGap: number
  rowGap: number
  colsPerRow: number
  printTitleAndSource: boolean
  title: string
  subtitle: string
  autoFill: boolean
}): EffectiveLayout {
  const paper = PAPER_SIZES_MM[store.paperSize] || PAPER_SIZES_MM.A4P
  const headerHeight = store.printTitleAndSource && (store.title || store.subtitle) ? 20 : 0
  
  const printableWidth = paper.width - store.marginLeft - store.marginRight
  const printableHeight = paper.height - store.marginTop - store.marginBottom - headerHeight

  if (!store.autoFill) {
    const rows = Math.floor((printableHeight + store.rowGap) / (store.gridSize + store.rowGap))
    return {
      cols: store.colsPerRow,
      rows: Math.max(1, rows),
      gridSize: store.gridSize,
      colGap: store.colGap,
      rowGap: store.rowGap,
      printableWidth,
      printableHeight,
    }
  }

  let cols = Math.floor(printableWidth / store.gridSize)
  cols = Math.max(1, cols)

  let rows = Math.floor(printableHeight / store.gridSize)
  rows = Math.max(1, rows)

  const gridSize = Math.min(printableWidth / cols, printableHeight / rows)

  return {
    cols,
    rows,
    gridSize,
    colGap: 0,
    rowGap: 0,
    printableWidth,
    printableHeight,
  }
}

export const useCopybookStore = create<CopybookState>()(
  persist(
    (set) => ({
      text: '永和九',
      setText: (text) => set({ text }),
      contentSource: 'custom',
      setContentSource: (contentSource) => set({ contentSource }),
      activeContentId: null,
      setActiveContentId: (activeContentId) => set({ activeContentId }),
      title: '',
      setTitle: (title) => set({ title }),
      subtitle: '',
      setSubtitle: (subtitle) => set({ subtitle }),
      footer: '',
      setFooter: (footer) => set({ footer }),
      printTitleAndSource: false,
      setPrintTitleAndSource: (printTitleAndSource) => set({ printTitleAndSource }),

      paperSize: 'A4P',
      setPaperSize: (paperSize) => set({ paperSize }),
      marginTop: 10,
      setMarginTop: (marginTop) => set({ marginTop }),
      marginBottom: 10,
      setMarginBottom: (marginBottom) => set({ marginBottom }),
      marginLeft: 10,
      setMarginLeft: (marginLeft) => set({ marginLeft }),
      marginRight: 10,
      setMarginRight: (marginRight) => set({ marginRight }),
      showPageNumber: false,
      setShowPageNumber: (showPageNumber) => set({ showPageNumber }),

      gridType: 'tian',
      setGridType: (gridType) => set({ gridType }),
      gridSize: 18,
      setGridSize: (gridSize) => set({ gridSize }),
      colsPerRow: 10,
      setColsPerRow: (colsPerRow) => set({ colsPerRow }),
      rowGap: 1,
      setRowGap: (rowGap) => set({ rowGap }),
      colGap: 1,
      setColGap: (colGap) => set({ colGap }),
      gridOpacity: 50,
      setGridOpacity: (gridOpacity) => set({ gridOpacity }),
      gridRadius: 0,
      setGridRadius: (gridRadius) => set({ gridRadius }),
      gridLineWidth: 0.5,
      setGridLineWidth: (gridLineWidth) => set({ gridLineWidth }),
      gridColor: '#9ca3af',
      setGridColor: (gridColor) => set({ gridColor }),

      showStroke: false,
      setShowStroke: (showStroke) => set({ showStroke }),
      strokeType: 'color',
      setStrokeType: (strokeType) => set({ strokeType }),
      strokeRepeat: 1,
      setStrokeRepeat: (strokeRepeat) => set({ strokeRepeat }),
      highlightLastStroke: false,
      setHighlightLastStroke: (highlightLastStroke) => set({ highlightLastStroke }),

      showTrace: true,
      setShowTrace: (showTrace) => set({ showTrace }),
      traceType: 'hollow',
      setTraceType: (traceType) => set({ traceType }),
      traceColor: '#f97316',
      setTraceColor: (traceColor) => set({ traceColor }),
      traceLineWidth: 1,
      setTraceLineWidth: (traceLineWidth) => set({ traceLineWidth }),
      exampleColor: '#808080',
      setExampleColor: (exampleColor) => set({ exampleColor }),
      traceInterval: false,
      setTraceInterval: (traceInterval) =>
        set(() => ({
          traceInterval,
          ...(traceInterval ? { copyInterval: false, copyRowInterval: false } : {}),
        })),
      copyInterval: false,
      setCopyInterval: (copyInterval) =>
        set(() => ({
          copyInterval,
          ...(copyInterval ? { traceInterval: false, copyRowInterval: false } : {}),
        })),
      copyBlankCount: 1,
      setCopyBlankCount: (copyBlankCount) =>
        set({ copyBlankCount: Math.min(6, Math.max(1, Math.floor(copyBlankCount))) }),
      copyRowInterval: false,
      setCopyRowInterval: (copyRowInterval) =>
        set(() => ({
          copyRowInterval,
          ...(copyRowInterval ? { copyInterval: false, traceInterval: false } : {}),
        })),
      copyRowBlankCount: 1,
      setCopyRowBlankCount: (copyRowBlankCount) =>
        set({ copyRowBlankCount: Math.min(6, Math.max(1, Math.floor(copyRowBlankCount))) }),

      showPhonetic: false,
      setShowPhonetic: (showPhonetic) => set({ showPhonetic }),
      phoneticBlank: false,
      setPhoneticBlank: (phoneticBlank) => set({ phoneticBlank }),

      showCorrection: false,
      setShowCorrection: (showCorrection) => set({ showCorrection }),
      fillLastPage: false,
      setFillLastPage: (fillLastPage) => set({ fillLastPage }),
      autoFill: true,
      setAutoFill: (autoFill) => set({ autoFill }),
      preserveLineBreaks: false,
      setPreserveLineBreaks: (preserveLineBreaks) => set({ preserveLineBreaks }),
      filterNewlinesOnClean: false,
      setFilterNewlinesOnClean: (filterNewlinesOnClean) => set({ filterNewlinesOnClean }),

      fontFamily: DEFAULT_COPYBOOK_FONT,
      setFontFamily: (fontFamily) => set({ fontFamily }),
      fontDirection: 'horizontal',
      setFontDirection: (fontDirection) => set({ fontDirection }),

      activeTemplateId: null,
      applyTemplate: (template) =>
        set({
          activeTemplateId: template.id,
          paperSize: 'A4P',
          gridType: template.gridType,
          showTrace: template.showTrace,
          traceType: template.traceType,
          showStroke: template.showStroke,
          showPhonetic: template.showPhonetic,
          showCorrection: template.showCorrection,
          colsPerRow: template.colsPerRow,
          gridSize: template.gridSize,
          subtitle: template.subtitle ?? '',
          footer: template.footer ?? '',
          printTitleAndSource: false,
          autoFill: true,
          fontFamily: DEFAULT_COPYBOOK_FONT,
          fontDirection: 'horizontal',
          copyInterval: template.copyInterval ?? false,
          copyBlankCount: template.copyBlankCount ?? 1,
          copyRowInterval: template.copyRowInterval ?? false,
          copyRowBlankCount: template.copyRowBlankCount ?? 1,
          ...(template.id === 'blank-practice' ? { text: '', activeContentId: null, contentSource: 'custom' } : {}),
        }),
      clearTemplateSelection: () => set({ activeTemplateId: null }),

      disabledFontIds: [],
      toggleFontDisabled: (id) =>
        set((state) => ({
          disabledFontIds: state.disabledFontIds.includes(id)
            ? state.disabledFontIds.filter((fid) => fid !== id)
            : [...state.disabledFontIds, id],
        })),
      localFonts: [],
      addLocalFont: (name, fontFamily) =>
        set((state) => ({
          localFonts: [
            ...state.localFonts,
            { id: 'local-' + Date.now(), name, fontFamily },
          ],
        })),
      removeLocalFont: (id) =>
        set((state) => ({
          localFonts: state.localFonts.filter((f) => f.id !== id),
        })),

      userItems: [],
      addUserItem: (item) =>
        set((state) => ({ userItems: [...state.userItems, item] })),
      updateUserItem: (id, updates) =>
        set((state) => ({
          userItems: state.userItems.map((u) => (u.id === id ? { ...u, ...updates } : u)),
        })),
      deleteUserItem: (id) =>
        set((state) => ({ userItems: state.userItems.filter((u) => u.id !== id) })),

      advancedMode: false,
      setAdvancedMode: (advancedMode) => set({ advancedMode }),
    }),
    {
      name: 'copybook-settings',
      version: 5,
      migrate: (persistedState) => {
        const state = persistedState as Partial<CopybookState>

        const nextState = {
          ...state,
          fontDirection: state.fontDirection ?? 'horizontal',
          copyBlankCount: state.copyBlankCount ?? 1,
          copyRowInterval: state.copyRowInterval ?? false,
          copyRowBlankCount: state.copyRowBlankCount ?? 1,
          gridColor: state.gridColor ?? '#9ca3af',
          preserveLineBreaks: state.preserveLineBreaks ?? true,
          filterNewlinesOnClean: state.filterNewlinesOnClean ?? false,
        }

        if (!nextState.fontFamily || nextState.fontFamily.includes('Noto Sans TC')) {
          nextState.fontFamily = DEFAULT_COPYBOOK_FONT
        }

        // Migrate default example color to 50% gray if it was the old blue default
        if (state.exampleColor === '#2563eb') {
          nextState.exampleColor = '#808080'
        }

        return nextState
      },
    }
  )
)

export function getGridChars(
  text: string,
  options?: {
    preserveLineBreaks?: boolean
    lineLength?: number
    copyInterval?: boolean
    copyBlankCount?: number
    copyRowInterval?: boolean
    copyRowBlankCount?: number
    showTrace?: boolean
    traceInterval?: boolean
  }
): string[] {
  const {
    preserveLineBreaks = true,
    lineLength = 10,
    copyInterval = false,
    copyBlankCount = 1,
    copyRowInterval = false,
    copyRowBlankCount = 1,
    showTrace = false,
    traceInterval = false,
  } = options || {}

  // 正規化換行符號（含字串轉義之 \n）
  const normalizedText = (text || '').replace(/\\n/g, '\n')
  const safeBlank = Math.min(6, Math.max(1, Math.floor(copyBlankCount)))
  const safeRowBlank = Math.min(6, Math.max(1, Math.floor(copyRowBlankCount)))
  const effLineLength = Math.max(1, lineLength || 10)

  // 一行字空 N 行模式
  if (copyRowInterval) {
    const result: string[] = []
    const rawLines = preserveLineBreaks ? normalizedText.split(/\r?\n/) : [normalizedText]

    for (let i = 0; i < rawLines.length; i++) {
      const rawLine = rawLines[i]
      const validChars = rawLine.split('').filter((c) => !/[\s\u3000\p{P}\p{S}]/u.test(c))

      if (validChars.length === 0) {
        if (preserveLineBreaks && i < rawLines.length - 1 && result.length > 0) {
          for (let k = 0; k < effLineLength; k++) {
            result.push('')
          }
        }
        continue
      }

      // 將該行文字按每行字數（effLineLength）分段
      for (let c = 0; c < validChars.length; c += effLineLength) {
        const chunk = validChars.slice(c, c + effLineLength)
        // 放入本行文字
        for (let j = 0; j < chunk.length; j++) {
          result.push(chunk[j])
        }
        // 若該行未排滿，自動補空格直到整行結束
        if (chunk.length < effLineLength) {
          const paddingNeeded = effLineLength - chunk.length
          for (let p = 0; p < paddingNeeded; p++) {
            result.push('')
          }
        }
        // 在文字行下方插入 N 行空白行
        const blankCells = safeRowBlank * effLineLength
        for (let b = 0; b < blankCells; b++) {
          result.push('')
        }
      }
    }
    return result
  }

  if (!preserveLineBreaks || !lineLength || lineLength <= 0) {
    // 連續排版模式
    const validChars = normalizedText.split('').filter((c) => !/[\s\u3000\p{P}\p{S}]/u.test(c))
    let base = validChars
    if (copyInterval) {
      base = base.flatMap((char) => [char, ...Array(safeBlank).fill('')])
    } else if (showTrace && traceInterval) {
      base = base.flatMap((char) => [char, char])
    }
    return base
  }

  // 保留換行模式：遇換行跳格對齊下一行／列
  const lines = normalizedText.split(/\r?\n/)
  const result: string[] = []

  for (let i = 0; i < lines.length; i++) {
    const rawLine = lines[i]
    const validChars = rawLine.split('').filter((c) => !/[\s\u3000\p{P}\p{S}]/u.test(c))

    if (validChars.length === 0) {
      // 空白行留空一行
      if (i < lines.length - 1 && result.length > 0) {
        for (let k = 0; k < lineLength; k++) {
          result.push('')
        }
      }
      continue
    }

    let lineChars = validChars
    if (copyInterval) {
      lineChars = lineChars.flatMap((char) => [char, ...Array(safeBlank).fill('')])
    } else if (showTrace && traceInterval) {
      lineChars = lineChars.flatMap((char) => [char, char])
    }

    for (let c = 0; c < lineChars.length; c++) {
      result.push(lineChars[c])
    }

    // 換行時，若該行未排滿，自動補空格直到整行結束，讓下一行從新行開始
    const remainder = result.length % lineLength
    if (remainder !== 0 && i < lines.length - 1) {
      const paddingNeeded = lineLength - remainder
      for (let p = 0; p < paddingNeeded; p++) {
        result.push('')
      }
    }
  }

  return result
}

export function expandCopyIntervalChars(chars: string[], blankCount: number): string[] {
  const safeBlankCount = Math.min(6, Math.max(1, Math.floor(blankCount)))
  return chars.flatMap((char) => [char, ...Array(safeBlankCount).fill('')])
}
